/**
 * Volume Profile (VPVR) binning — pure math over the bars on the chart.
 *
 * Volume-at-price from OHLCV can only be approximated: without tick data we
 * spread each bar's volume UNIFORMLY across the price buckets its high–low
 * span overlaps (proportional to the overlap), the industry-standard OHLCV
 * approximation. A zero-range bar drops all its volume into its close's
 * bucket. up/down is split by `close >= open`.
 *
 * Kept side-effect-free and independent of klinecharts so it unit-tests in
 * isolation, mirroring computePivotPoints.
 */

import type { BarVolumeHistogram } from '../types/chart'
import { expandValueArea, pointOfControl } from './profileValueArea'

export type { BarVolumeHistogram }

export interface ProfileBar {
  open: number
  high: number
  low: number
  close: number
  volume?: number
  /** Optional finer-than-bar volume distribution. Absent → uniform spread. */
  vp?: BarVolumeHistogram
}

/** Bar shape the collection helper reads — klinecharts' KLineData fits. */
export interface OhlcvBar {
  timestamp: number
  open: number
  high: number
  low: number
  close: number
  volume?: number
}

/** ProfileBar plus the timestamp session logic keys off (TpoBar-compatible). */
export interface SessionProfileBar extends ProfileBar {
  timestamp: number
}

/**
 * Collect `[start, endExclusive)` into profile bars, skipping holes and any
 * bar `keep` rejects — the shared preamble of every profile draw.
 */
export function collectProfileBars(
  list: readonly (OhlcvBar | undefined)[],
  start: number,
  endExclusive: number,
  keep?: (timestamp: number) => boolean,
): SessionProfileBar[] {
  const bars: SessionProfileBar[] = []
  for (let i = start; i < endExclusive; i++) {
    const d = list[i]
    if (!d || (keep && !keep(d.timestamp))) continue
    bars.push({
      timestamp: d.timestamp,
      open: d.open,
      high: d.high,
      low: d.low,
      close: d.close,
      volume: d.volume ?? 0,
    })
  }
  return bars
}

/** Usable only when both arrays are present, equal-length, and non-empty. */
function usableHistogram(bar: ProfileBar): BarVolumeHistogram | null {
  const vp = bar.vp
  if (!vp) return null
  if (!Array.isArray(vp.u) || !Array.isArray(vp.d)) return null
  if (vp.u.length === 0 || vp.u.length !== vp.d.length) return null
  return vp
}

/** A bar's volume as the profile should count it — the histogram when present
 *  (so bucket totals and the value-area denominator can never disagree, even if
 *  the server's rounding drifts), else the plain `volume` field. */
function effectiveVolume(bar: ProfileBar): number {
  const vp = usableHistogram(bar)
  if (!vp) return bar.volume ?? 0
  let sum = 0
  for (let i = 0; i < vp.u.length; i++) sum += vp.u[i] + vp.d[i]
  return sum
}

export interface VolumeBucket {
  /** Inclusive lower price edge of the bucket. */
  priceLow: number
  /** Exclusive upper price edge (except the top bucket, which is inclusive). */
  priceHigh: number
  total: number
  up: number
  down: number
}

export interface VolumeProfile {
  buckets: VolumeBucket[]
  /** Index of the max-total bucket (point of control). */
  pocIdx: number
  /** Value-area high edge — highest-priced bucket inside the value area. */
  vahIdx: number
  /** Value-area low edge — lowest-priced bucket inside the value area. */
  valIdx: number
  totalVolume: number
}

export interface BinOptions {
  rows: number
  /** Percent of total volume the value area must cover (default 70). */
  valueAreaPct?: number
}

/**
 * Bin the given bars into a volume profile. Returns null when there is nothing
 * meaningful to draw: fewer than 2 bars, zero total volume, or a degenerate
 * (zero-width) price domain. The caller's draw skips on null.
 */
export function binVolumeByPrice(bars: ProfileBar[], opts: BinOptions): VolumeProfile | null {
  if (bars.length < 2) return null

  const rows = Math.max(1, Math.floor(opts.rows))

  let priceMin = Infinity
  let priceMax = -Infinity
  let totalVolume = 0
  for (const b of bars) {
    if (b.low < priceMin) priceMin = b.low
    if (b.high > priceMax) priceMax = b.high
    totalVolume += effectiveVolume(b)
  }
  if (totalVolume <= 0) return null
  const domain = priceMax - priceMin
  if (domain <= 0) return null

  return binUniformGrid(bars, priceMin, domain / rows, rows, opts.valueAreaPct ?? 70, totalVolume)
}

/** Grid a companion profile must bin onto — see `binVolumeOnGrid`. */
export interface GridBinOptions {
  /** Lowest row edge. */
  origin: number
  /** Row height in price. */
  tickSize: number
  rowCount: number
  /** Percent of total volume the value area must cover (default 70). */
  valueAreaPct?: number
}

/**
 * Bin volume onto a CALLER-SUPPLIED price grid rather than the bars' own domain.
 *
 * This is what lets the volume band beside a TPO share the profile's exact rows.
 * Binning it independently would divide the same price range into its own equal
 * buckets, and because the TPO grid is snapped to absolute tick multiples the two
 * ladders would sit up to a tick apart — so a volume bar and the TPO row it is
 * supposed to correspond to would not line up, defeating the side-by-side read.
 */
export function binVolumeOnGrid(bars: ProfileBar[], opts: GridBinOptions): VolumeProfile | null {
  const rows = Math.max(1, Math.floor(opts.rowCount))
  if (!(opts.tickSize > 0) || !Number.isFinite(opts.origin)) return null

  let totalVolume = 0
  for (const b of bars) totalVolume += effectiveVolume(b)
  if (totalVolume <= 0) return null

  return binUniformGrid(
    bars,
    opts.origin,
    opts.tickSize,
    rows,
    opts.valueAreaPct ?? 70,
    totalVolume,
  )
}

/**
 * Shared binner: spread every bar's volume across a uniform price grid.
 *
 * Both entry points funnel through here so the finer-than-bar `vp` histogram
 * handling, the up/down split and the value-area growth cannot fork between the
 * standalone profiles and the TPO companion band.
 */
function binUniformGrid(
  bars: ProfileBar[],
  priceMin: number,
  h: number,
  rows: number,
  valueAreaPct: number,
  totalVolume: number,
): VolumeProfile | null {
  const buckets: VolumeBucket[] = Array.from({ length: rows }, (_, i) => ({
    priceLow: priceMin + i * h,
    priceHigh: priceMin + (i + 1) * h,
    total: 0,
    up: 0,
    down: 0,
  }))

  // Clamp a price to a valid bucket index — the top edge (priceMax) folds into
  // the last bucket rather than overflowing.
  const bucketIndexOf = (price: number): number => {
    const idx = Math.floor((price - priceMin) / h)
    return idx < 0 ? 0 : idx >= rows ? rows - 1 : idx
  }

  const add = (i: number, vol: number, isUp: boolean) => {
    const bucket = buckets[i]
    bucket.total += vol
    if (isUp) bucket.up += vol
    else bucket.down += vol
  }

  /**
   * Spread `vol` uniformly across the profile buckets overlapping
   * [srcLow, srcHigh], proportional to each bucket's share of that span.
   * A zero-width source drops everything into the bucket holding `srcLow`.
   */
  const gridLow = priceMin
  const gridHigh = priceMin + rows * h

  const spread = (srcLow: number, srcHigh: number, vol: number, isUp: boolean) => {
    if (vol <= 0) return
    if (srcHigh <= srcLow) {
      add(bucketIndexOf(srcLow), vol, isUp)
      return
    }

    /**
     * Clamped to the grid, and weighted by the CLAMPED span.
     *
     * `binVolumeByPrice` always derives the grid from the same bars, so the grid
     * covers them and this is a no-op. `binVolumeOnGrid` takes the grid from the
     * caller, where it need not: weighting by the original span there left the
     * out-of-grid share unassigned and quietly deleted it, and because the totals
     * are what the POC and value area are computed from, a band over a tight grid
     * would have reported a confidently wrong POC.
     */
    const lo = srcLow < gridLow ? gridLow : srcLow
    const hi = srcHigh > gridHigh ? gridHigh : srcHigh
    if (hi <= lo) {
      // Entirely outside the grid — park it in the nearest end row, which is what
      // `bucketIndexOf` clamping already implies for a single price.
      add(bucketIndexOf(srcLow), vol, isUp)
      return
    }

    const span = hi - lo
    const first = bucketIndexOf(lo)
    const last = bucketIndexOf(hi)
    for (let i = first; i <= last; i++) {
      const bucket = buckets[i]
      const overlap = Math.min(hi, bucket.priceHigh) - Math.max(lo, bucket.priceLow)
      if (overlap <= 0) continue
      add(i, (vol * overlap) / span, isUp)
    }
  }

  for (const b of bars) {
    const vp = usableHistogram(b)

    if (vp) {
      // Finer path: replay the bar's OWN distribution. Each of its K sub-ranges
      // is spread separately, carrying its own up/down split — so a bar that
      // closed down still contributes up-volume wherever buying actually
      // happened inside it.
      const k = vp.u.length
      if (b.high <= b.low) {
        // Degenerate bar: the sub-ranges collapse onto one price.
        let u = 0
        let d = 0
        for (let i = 0; i < k; i++) {
          u += vp.u[i]
          d += vp.d[i]
        }
        spread(b.close, b.close, u, true)
        spread(b.close, b.close, d, false)
        continue
      }
      const hb = (b.high - b.low) / k
      for (let i = 0; i < k; i++) {
        const subLow = b.low + i * hb
        const subHigh = subLow + hb
        spread(subLow, subHigh, vp.u[i], true)
        spread(subLow, subHigh, vp.d[i], false)
      }
      continue
    }

    // Fallback: uniform across the whole bar, up/down from `close >= open`.
    // This is the K=1 case of the path above.
    spread(b.low, b.high, b.volume ?? 0, b.close >= b.open)
  }

  // POC + value area come from the shared profile math — TPO/market profile
  // grows its band identically over a different counter, so the logic lives in
  // profileValueArea.ts rather than being forked per family.
  const totals = buckets.map((b) => b.total)
  const pocIdx = pointOfControl(totals)
  const { valIdx, vahIdx } = expandValueArea(totals, pocIdx, (valueAreaPct / 100) * totalVolume)

  return { buckets, pocIdx, vahIdx, valIdx, totalVolume }
}
